/**
 * Centralized storage utility for consistent localStorage and sessionStorage access
 * Handles SSR compatibility and provides a unified API
 */

// Helper to safely check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && window.localStorage && window.sessionStorage;

// Generic storage interface - provides both localStorage API and shorthand methods
const createStorageInterface = (storageType) => ({
  // Standard localStorage API
  getItem(key) {
    if (!isBrowser) return null;
    try {
      return storageType.getItem(key);
    } catch (error) {
      console.error(`Error reading from storage:`, error);
      return null;
    }
  },

  setItem(key, value) {
    if (!isBrowser) return;
    try {
      // Convert objects/arrays to JSON strings
      const valueToStore = typeof value === 'object' && value !== null 
        ? JSON.stringify(value) 
        : value;
      storageType.setItem(key, valueToStore);
    } catch (error) {
      console.error(`Error writing to storage:`, error);
    }
  },

  removeItem(key) {
    if (!isBrowser) return;
    try {
      storageType.removeItem(key);
    } catch (error) {
      console.error(`Error removing from storage:`, error);
    }
  },

  clear() {
    if (!isBrowser) return;
    try {
      storageType.clear();
    } catch (error) {
      console.error(`Error clearing storage:`, error);
    }
  },

  // Shorthand methods for easier use
  get(key) {
    return this.getItem(key);
  },

  set(key, value) {
    return this.setItem(key, value);
  },

  remove(key) {
    return this.removeItem(key);
  }
});

// Mock storage for SSR
const mockStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
  clear: () => {}
};

// Create storage interfaces
const localStorage = createStorageInterface(isBrowser ? window.localStorage : mockStorage);
const sessionStorage = createStorageInterface(isBrowser ? window.sessionStorage : mockStorage);

// Migration helper - move data from localStorage to sessionStorage
const migrateCheckoutData = () => {
  if (!isBrowser) return;
  
  const keysToMigrate = [
    'cartDetail',
    'cartItems',
    'customerFormData',
    'businessFormData',
    'checkout_phase',
    'terrace_finance_data',
    'terrace_finance_state',
    'terrace_application_id',
    'terrace_finance_application_id',
    'terrace_finance_stage',
    'terrace_offers',
    'terrace_selected_offer',
    'terrace_order_pids',
    'order_pids',
    'orders',
    'checkout_url',
    'userDetail',
    'customerId',
    'companyId',
    'paymentType',
    'setPreCheckout',
    'taxAmount',
    'termLabel',
    'session_id'
  ];
  
  keysToMigrate.forEach(key => {
    const value = window.localStorage.getItem(key);
    if (value) {
      window.sessionStorage.setItem(key, value);
      window.localStorage.removeItem(key);
    }
  });
};

// Clear all checkout-related data
const clearCheckoutData = () => {
  if (!isBrowser) return;
  
  const keysToClear = [
    'cartDetail',
    'cartItems', 
    'customerFormData',
    'businessFormData',
    'checkout_phase',
    'terrace_finance_data',
    'terrace_finance_state',
    'terrace_application_id',
    'terrace_finance_application_id',
    'terrace_finance_stage',
    'terrace_offers',
    'terrace_selected_offer',
    'terrace_order_pids',
    'order_pids',
    'orders',
    'checkout_url',
    'userDetail',
    'customerId',
    'companyId',
    'paymentType',
    'setPreCheckout',
    'taxAmount',
    'termLabel',
    'session_id'
  ];
  
  keysToClear.forEach(key => {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  });
  
  // Reset grenke_state isCartLimitExceed flag when clearing checkout data
  // This prevents the flag from persisting across different checkout sessions
  try {
    const grenkeState = window.localStorage.getItem("grenke_state");
    if (grenkeState) {
      const state = JSON.parse(grenkeState);
      // Reset the flag but keep other grenke_state data
      state.isCartLimitExceed = 0;
      state.lastUpdated = Date.now();
      window.localStorage.setItem("grenke_state", JSON.stringify(state));
    }
  } catch (error) {
    console.warn("Error resetting grenke_state isCartLimitExceed flag:", error);
  }
};

// Export storage utilities
export const storage = {
  local: localStorage,
  session: sessionStorage,
  migrateCheckoutData,
  clearCheckoutData
};

// Default export for convenience
export default storage;